import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { Card, Btn, Field, Input, Select, Th, Badge, fmtN, useMasters, MasterOptions } from "./camCommon";

const EMPTY = {
  deal_no: "", mode: "", from_location: "", to_location: "",
  nominated_volume: "", actual_volume: "0", scheduled_date: "", actual_date: "", status: "nominated",
};

export default function CamMovements() {
  const { byKind } = useMasters();
  const [moves, setMoves] = useState([]);
  const [deals, setDeals] = useState([]);
  const [fStatus, setFStatus] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/cam/movements${fStatus ? `?status=${fStatus}` : ""}`)
      .then((r) => setMoves(r.data || [])).catch(() => {});
  }, [fStatus]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/cam/deals").then((r) => setDeals(r.data || [])).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/cam/movements", form);
      toast.success(form.id ? "Movement updated" : "Movement created");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete movement ${m.movement_no}?`)) return;
    try {
      await api.delete(`/cam/movements/${m.id}`);
      toast.success("Movement deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="cam-movements">
      <Card title="Scheduling & Logistics" subtitle="Nominate and track physical movements — pipeline, vessel, rail & truck — against deals"
        actions={
          <div className="flex items-center gap-2">
            <Select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="!w-36" data-testid="move-filter-status">
              <option value="">All statuses</option>
              {["nominated", "in_transit", "delivered", "reconciled"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </Select>
            <Btn onClick={() => setForm({ ...EMPTY })} data-testid="move-new-btn">
              <Plus size={13} className="inline mr-1" /> New Movement
            </Btn>
          </div>
        } testid="cam-movements-list-card">

        {form && (
          <div className="mb-5 border border-[#C8A24B]/40 bg-amber-50/30 rounded-sm p-4" data-testid="move-form">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0B1E3F]">
                {form.id ? `Edit movement ${form.movement_no}` : "New movement"}
              </div>
              <button onClick={() => setForm(null)} data-testid="move-form-close" className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Deal *">
                <Select value={form.deal_no} onChange={set("deal_no")} data-testid="move-deal">
                  <option value="">Select deal…</option>
                  {deals.map((d) => (
                    <option key={d.deal_no} value={d.deal_no}>
                      {d.deal_no} — {d.side} {d.commodity} {fmtN(d.volume)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Transport Mode *">
                <Select value={form.mode} onChange={set("mode")} data-testid="move-mode">
                  <MasterOptions items={byKind("transport_mode")} />
                </Select>
              </Field>
              <Field label="From Location *">
                <Select value={form.from_location} onChange={set("from_location")} data-testid="move-from">
                  <MasterOptions items={byKind("location")} />
                </Select>
              </Field>
              <Field label="To Location *">
                <Select value={form.to_location} onChange={set("to_location")} data-testid="move-to">
                  <MasterOptions items={byKind("location")} />
                </Select>
              </Field>
              <Field label="Nominated Volume *">
                <Input type="number" min="0" value={form.nominated_volume} onChange={set("nominated_volume")} data-testid="move-nominated" />
              </Field>
              <Field label="Actual Volume">
                <Input type="number" min="0" value={form.actual_volume} onChange={set("actual_volume")} data-testid="move-actual" />
              </Field>
              <Field label="Scheduled Date *">
                <Input type="date" value={form.scheduled_date} onChange={set("scheduled_date")} data-testid="move-sched-date" />
              </Field>
              <Field label="Actual Date">
                <Input type="date" value={form.actual_date} onChange={set("actual_date")} data-testid="move-actual-date" />
              </Field>
              <Field label="Status">
                <Select value={form.status} onChange={set("status")} data-testid="move-status">
                  {["nominated", "in_transit", "delivered", "reconciled"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </Select>
              </Field>
            </div>
            <div className="mt-4">
              <Btn onClick={save} disabled={saving} data-testid="move-save-btn">
                {saving ? "Saving…" : form.id ? "Update Movement" : "Create Movement"}
              </Btn>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <Th>Movement</Th><Th>Deal</Th><Th>Commodity</Th><Th>Mode</Th><Th>Route</Th>
                <Th className="text-right">Nominated</Th><Th className="text-right">Actual</Th>
                <Th>Scheduled</Th><Th>Delivered</Th><Th>Status</Th><Th className="w-16" />
              </tr>
            </thead>
            <tbody>
              {moves.map((m) => (
                <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`move-row-${m.movement_no}`}>
                  <td className="py-2 pr-3 font-mono text-xs font-semibold text-[#0B1E3F]">{m.movement_no}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{m.deal_no}</td>
                  <td className="py-2 pr-3 font-semibold">{m.commodity}</td>
                  <td className="py-2 pr-3 text-xs">{m.mode}</td>
                  <td className="py-2 pr-3 text-xs text-slate-600">{m.from_location} → {m.to_location}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtN(m.nominated_volume)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{m.actual_volume ? fmtN(m.actual_volume) : "—"}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{m.scheduled_date}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{m.actual_date || "—"}</td>
                  <td className="py-2 pr-3"><Badge v={m.status} /></td>
                  <td className="py-2">
                    <div className="flex gap-1.5">
                      <button onClick={() => setForm({ ...m })} data-testid={`move-edit-${m.movement_no}`}
                        className="text-slate-400 hover:text-[#0B1E3F]" title="Edit"><PencilSimple size={15} /></button>
                      <button onClick={() => remove(m)} data-testid={`move-delete-${m.movement_no}`}
                        className="text-slate-400 hover:text-red-600" title="Delete"><Trash size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {moves.length === 0 && (
                <tr><td colSpan={11} className="py-6 text-center text-slate-400 text-xs" data-testid="moves-empty">
                  No movements — nominate one against a deal.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
